/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { Grid, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Sanitize } from '../../../Components/Sanitizer';
import './styles.scss';
import InputField from '../../../Components/FormFields/InputField';
import SelectField from '../../../Components/FormFields/SelectField';
import CheckboxField from '../../../Components/FormFields/CheckboxField';
import {
  DatePickerField,
} from '../../../Components/FormFields/DatePickerField';
import IbanField from '../../../Components/FormFields/IbanField';

/**
 * UserForm Component
 * @param props
 * @returns {Element}
 * @constructor
 */
export default function UserForm(props) {
  const { values } = useFormikContext();
  const { t } = useTranslation();
  const dateBefore18Years = dayjs().subtract(18, 'year');

  const {
    validateField,
    thirdStepContent,
    resetValidSteps,
    marketingConsent,
    marketingConsentIsRequired,
    privacyPolicyConsent,
    formField: {
      firstName,
      lastName,
      street,
      houseNumber,
      postCode,
      city,
      email,
      phonePrefix,
      phone,
      marketingTerms,
      generalTerms,
      gender,
      iban,
      dateOfBirth,
    },
  } = props;

  const handlePrivatePolicyConsentChange = (e) => {
    if (!e.target?.checked) {
      resetValidSteps();
    }
  };
  const handleMarketingConsentChange = (e) => {
    if (!e.target?.checked && marketingConsentIsRequired) {
      resetValidSteps();
    }
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>{t('currentChoice')}</Typography>
          <Typography variant="subtitle2">
            {t('yourChoice')}
            {' '}
            {values.studioName}
          </Typography>
          <Typography variant="subtitle2">
            {t('yourPackage')}
            {' '}
            {values.contractName}
            {' '}
            -
            {' '}
            {values.contractTerm.price}
            {' '}
            {t('euroPerMonth')}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Sanitize value={thirdStepContent} />
        <Grid item xs={12}>
          <InputField name={firstName.name} label={firstName.label} fullWidth required />
        </Grid>
        <Grid item xs={12}>
          <InputField name={lastName.name} label={lastName.label} fullWidth required />
        </Grid>
        <Grid item xs={12}>
          <SelectField
            name={gender.name}
            label={gender.label}
            data={gender.data}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <InputField name={street.name} label={street.label} fullWidth required />
        </Grid>
        <Grid item xs={12}>
          <InputField name={houseNumber.name} label={houseNumber.label} fullWidth required />
        </Grid>
        <Grid item xs={12}>
          <InputField name={postCode.name} label={postCode.label} fullWidth required />
        </Grid>
        <Grid item xs={12}>
          <InputField name={city.name} label={city.label} fullWidth required />
        </Grid>
        <Grid item xs={12}>
          <InputField name={email.name} label={email.label} fullWidth required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SelectField
            name={phonePrefix.name}
            label={phonePrefix.label}
            data={phonePrefix.data}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputField name={phone.name} label={phone.label} fullWidth required />
        </Grid>
        <Grid item xs={12}>
          <DatePickerField
            sx={{ width: '100%' }}
            required
            label={dateOfBirth.label}
            maxDate={dateBefore18Years}
            name={dateOfBirth.name}
          />
        </Grid>
        <Grid item xs={12}>
          <IbanField name={iban.name} label={iban.label} validateField={validateField} required fullWidth />
        </Grid>
        <Grid item xs={12}>
          <CheckboxField
            name={generalTerms.name}
            label={<Sanitize value={privacyPolicyConsent} />}
            required
            checkedBefore={values.generalTerms}
            onChange={handlePrivatePolicyConsentChange}
          />
        </Grid>
        {marketingConsent !== '' ? (
          <Grid item xs={12}>
            <CheckboxField
              name={marketingTerms.name}
              label={<Sanitize value={marketingConsent} />}
              required={marketingConsentIsRequired}
              checkedBefore={values.marketingTerms}
              onChange={handleMarketingConsentChange}
            />
          </Grid>
        ) : null}
      </Grid>
    </>
  );
}

UserForm.propTypes = {
  validateField: PropTypes.func.isRequired,
  thirdStepContent: PropTypes.object.isRequired,
  resetValidSteps: PropTypes.func.isRequired,
  marketingConsent: PropTypes.string.isRequired,
  marketingConsentIsRequired: PropTypes.bool,
  privacyPolicyConsent: PropTypes.string.isRequired,
  formField: PropTypes.object.isRequired,
};

UserForm.defaultProps = {
  marketingConsentIsRequired: null,
};
